import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { CollectionsService } from '@/services/api/services/CollectionsService';
import type { Collection, CollectionDetail, CreateCollection, UpdateCollection, CreateCollectionQuery, UpdateCollectionQuery, CreateFolder, UpdateFolder } from '@/services/api';
import { dispatchCollectionEvent, COLLECTION_EVENTS } from '../utils/collectionEvents';

// Helper function to handle error type checking
function parseError(error: unknown, defaultMessage: string): string {
  if (error && typeof error === 'object') {
    const maybeResp = (error as { response?: unknown }).response
    if (maybeResp && typeof maybeResp === 'object' && 'status' in (maybeResp as { status?: unknown })) {
      const { status } = maybeResp as { status?: unknown }
      const { body } = error as { body?: unknown }
      const message = body && typeof body === 'object' && 'message' in (body as { message?: unknown }) && typeof (body as { message?: unknown }).message === 'string'
        ? (body as { message?: string }).message
        : undefined
      if (status === 400) return message || 'Invalid request data'
      if (status === 401) return 'Authentication required. Please log in again.'
      if (status === 403) return 'You do not have permission to perform this action.'
      if (status === 404) return 'Resource not found.'
      if (status === 409) return 'Resource already exists or conflict occurred.'
      if (typeof status === 'number' && status >= 500) return 'Server error. Please try again later.'
    }
    const maybeMessage = (error as { message?: unknown }).message
    if (typeof maybeMessage === 'string') return maybeMessage
  }
  return defaultMessage;
}

// Define cache invalidation helpers
interface CacheManager {
  invalidateCollection: (id: string) => void;
  invalidateAllCollections: () => void;
  getCollectionFromCache: (id: string) => CollectionDetail | null;
  setCollectionInCache: (id: string, data: CollectionDetail) => void;
}

const createCacheManager = (): CacheManager => {
  const cache = new Map<string, CollectionDetail>();
  
  return {
    invalidateCollection: (id: string) => cache.delete(id),
    invalidateAllCollections: () => { cache.clear() },
    getCollectionFromCache: (id: string) => cache.get(id) || null,
    setCollectionInCache: (id: string, data: CollectionDetail) => cache.set(id, data)
  };
};

interface CollectionsState {
  collections: Collection[];
  currentCollection: CollectionDetail | null;
  isLoading: boolean;
  error: string | null;
  // Add cache manager
  cache: CacheManager;
  // Track loading states more granularly
  loadingStates: Record<string, boolean>;
  // Track ongoing query updates to prevent duplicates
  updateQueryLocks: Set<string>;
  
  // Actions
  fetchCollections: (parentId?: string, includeStats?: boolean) => Promise<void>;
  refreshCollections: (parentId?: string, includeStats?: boolean) => Promise<void>;
  createCollection: (data: CreateCollection) => Promise<string | undefined>;
  updateCollection: (id: string, data: UpdateCollection) => Promise<void>;
  deleteCollection: (id: string, force?: boolean) => Promise<void>;
  
  // Collection details
  fetchCollectionDetails: (id: string, forceRefresh?: boolean) => Promise<void>;
  clearCurrentCollection: () => void;
  
  // Query management
  createQuery: (collectionId: string, data: CreateCollectionQuery) => Promise<string | undefined>;
  updateQuery: (collectionId: string, queryId: number, data: UpdateCollectionQuery) => Promise<void>;
  deleteQuery: (collectionId: string, queryId: number) => Promise<void>;
  moveQuery: (collectionId: string, queryId: number, targetCollectionId: string, targetFolderId?: string) => Promise<void>;
  
  // Folder management
  createFolder: (collectionId: string, data: CreateFolder) => Promise<string | undefined>;
  updateFolder: (collectionId: string, folderId: string, data: UpdateFolder) => Promise<void>;
  deleteFolder: (collectionId: string, folderId: string, force?: boolean) => Promise<void>;
  
  // Utility
  setError: (error: string | null) => void;
  clearError: () => void;
}

export const useCollectionsStore = create<CollectionsState>()(
  devtools(
    (set, get) => ({
      collections: [],
      currentCollection: null,
      isLoading: false,
      error: null,
      cache: createCacheManager(),
      loadingStates: {},
      updateQueryLocks: new Set(),

      fetchCollections: async (parentId?: string, includeStats = true) => {
        const state = get();
        const cacheKey = `collections-${parentId || 'root'}`;
        
        // Prevent duplicate requests
        if (state.loadingStates[cacheKey]) {
          return;
        }
        
        // If collections already exist and no parentId specified, don't refetch
        if (!parentId && state.collections.length > 0 && !state.error) {
          return;
        }
        
        set(state => ({ 
          ...state,
          isLoading: true, 
          error: null,
          loadingStates: { ...state.loadingStates, [cacheKey]: true }
        }));
        
        try {
          const response = await CollectionsService.listCollections({
            parentId,
            includeStats,
            limit: 100
          });
          
          set(state => ({ 
            ...state,
            collections: response.data || [], 
            isLoading: false,
            loadingStates: { ...state.loadingStates, [cacheKey]: false }
          }));
        } catch (error) {
          const errorMessage = parseError(error, 'Failed to fetch collections');
          
          set(state => ({ 
            ...state,
            error: errorMessage, 
            isLoading: false,
            loadingStates: { ...state.loadingStates, [cacheKey]: false }
          }));
        }
      },

      refreshCollections: async (parentId?: string, includeStats = true) => {
        const state = get();
        // Clear cache and force refresh
        state.cache.invalidateAllCollections();
        
        set(state => ({ 
          ...state,
          isLoading: true, 
          error: null,
          loadingStates: {} // Reset all loading states
        }));
        
        try {
          const response = await CollectionsService.listCollections({
            parentId,
            includeStats,
            limit: 100
          });
          set(state => ({
            ...state,
            collections: response.data || [], 
            isLoading: false
          }));
        } catch (error) {
          const errorMessage = parseError(error, 'Failed to fetch collections');
          
          set(state => ({ 
            ...state,
            error: errorMessage, 
            isLoading: false 
          }));
        }
      },

      createCollection: async (data: CreateCollection) => {
        // Validate input
        if (!data.name.trim()) {
          const error = new Error('Collection name is required');
          // Don't set global error state - let the form handle it
          throw error;
        }
        
        if (data.name.length > 100) {
          const error = new Error('Collection name must be 100 characters or less');
          // Don't set global error state - let the form handle it
          throw error;
        }
        
        set({ isLoading: true });
        try {
          const response = await CollectionsService.createCollection({ requestBody: data });
          
          // Try to refresh collections list, but don't fail if refresh fails
          try {
            await get().refreshCollections(data.parentCollectionId);
          } catch (refreshError) {
            // Log but don't propagate refresh errors - the creation succeeded
            console.error('Failed to refresh collections after creation:', refreshError);
            // Clear any error state that refreshCollections might have set
            set(state => ({ ...state, error: null }));
          }
          
          // Dispatch COLLECTION_CREATED event to notify UI components
          if (response.id) {
            dispatchCollectionEvent(COLLECTION_EVENTS.COLLECTION_CREATED, {
              collectionId: response.id
            });
          }
          
          set({ isLoading: false });
          return response.id;
        } catch (error) {
          const errorMessage = parseError(error, 'Failed to create collection');
          
          // Don't set global error state - just reset loading
          set({ isLoading: false });
          throw new Error(errorMessage);
        }
      },

      updateCollection: async (id: string, data: UpdateCollection) => {
        set({ isLoading: true });
        try {
          await CollectionsService.updateCollection({ collectionId: id, requestBody: data });
          
          // Try to refresh collections list, but don't fail if refresh fails
          try {
            await get().refreshCollections();
          } catch (refreshError) {
            console.error('Failed to refresh collections after update:', refreshError);
            // Clear any error state that refreshCollections might have set
            set(state => ({ ...state, error: null }));
          }
          
          // Refresh current collection if it's the one being updated
          if (get().currentCollection?.id === id) {
            try {
              await get().fetchCollectionDetails(id, true);
            } catch (detailsError) {
              console.error('Failed to refresh collection details:', detailsError);
            }
          }
          
          // Dispatch COLLECTION_UPDATED event to notify UI components
          dispatchCollectionEvent(COLLECTION_EVENTS.COLLECTION_UPDATED, {
            collectionId: id
          });
          
          set({ isLoading: false });
        } catch (error) {
          // Don't set global error state - just reset loading
          set({ isLoading: false });
          throw error;
        }
      },

      deleteCollection: async (id: string, force = false) => {
        set({ isLoading: true });
        try {
          await CollectionsService.deleteCollection({ collectionId: id, force });
          
          // Remove from local state
          set(state => ({
            collections: state.collections.filter(c => c.id !== id),
            currentCollection: state.currentCollection?.id === id ? null : state.currentCollection,
            isLoading: false
          }));
          
          // Dispatch COLLECTION_DELETED event to notify UI components
          dispatchCollectionEvent(COLLECTION_EVENTS.COLLECTION_DELETED, {
            collectionId: id
          });
        } catch (error) {
          // Don't set global error state - just reset loading
          set({ isLoading: false });
          throw error;
        }
      },

      fetchCollectionDetails: async (id: string, forceRefresh = false) => {
        const state = get();
        
        // Check cache first (unless force refresh is requested)
        if (!forceRefresh) {
          const cached = state.cache.getCollectionFromCache(id);
          if (cached) {
            set(state => ({ ...state, currentCollection: cached }));
            return;
          }
        }
        
        // Prevent duplicate requests
        const loadingKey = `collection-${id}`;
        if (state.loadingStates[loadingKey]) {
          return;
        }
        
        set(state => ({ 
          ...state,
          isLoading: true, 
          error: null,
          loadingStates: { ...state.loadingStates, [loadingKey]: true }
        }));
        
        try {
          const collection = await CollectionsService.getCollection({ collectionId: id });
          
          // Cache the result
          const currentState = get();
          currentState.cache.setCollectionInCache(id, collection);
          
          set(state => ({ 
            ...state,
            currentCollection: collection, 
            isLoading: false,
            loadingStates: { ...state.loadingStates, [loadingKey]: false }
          }));
        } catch (error) {
          set(state => ({ 
            ...state,
            error: error instanceof Error ? error.message : 'Failed to fetch collection details', 
            isLoading: false,
            loadingStates: { ...state.loadingStates, [loadingKey]: false }
          }));
        }
      },

      clearCurrentCollection: () => {
        set({ currentCollection: null });
      },

      createQuery: async (collectionId: string, data: CreateCollectionQuery) => {
        // Validate input
        if (!data.name.trim()) {
          const error = new Error('Query name is required');
          // Don't set global error state - let the form handle it
          throw error;
        }
        
        if (!data.query.trim()) {
          const error = new Error('Query content is required');
          // Don't set global error state - let the form handle it
          throw error;
        }
        
        if (!collectionId.trim()) {
          const error = new Error('Collection ID is required');
          // Don't set global error state - let the form handle it
          throw error;
        }
        
        set({ isLoading: true });
        try {
          const response = await CollectionsService.createCollectionQuery({ 
            collectionId, 
            requestBody: data 
          });
          
          // Invalidate cache and refresh collection details
          const state = get();
          state.cache.invalidateCollection(collectionId);
          await state.fetchCollectionDetails(collectionId);
          
          set({ isLoading: false });
          return response.id;
        } catch (error) {
          const errorMessage = parseError(error, 'Failed to create query');
          
          // Don't set global error state - just reset loading
          set({ isLoading: false });
          throw new Error(errorMessage);
        }
      },

      updateQuery: async (collectionId: string, queryId: number, data: UpdateCollectionQuery) => {
        // Create unique lock key for this specific query update
        const lockKey = `${collectionId}-${queryId}`;
        const state = get();
        
        // Check if this query is already being updated
        if (state.updateQueryLocks.has(lockKey)) {
          return;
        }
        
        // Add to locks
        set(state => ({ 
          ...state, 
          updateQueryLocks: new Set([...state.updateQueryLocks, lockKey]),
          isLoading: true
        }));
        
        try {
          await CollectionsService.updateCollectionQuery({ 
            collectionId, 
            queryId, 
            requestBody: data 
          });
          
          // Invalidate cache and don't auto-refresh - let event system handle it
          const currentState = get();
          currentState.cache.invalidateCollection(collectionId);
          
          // Remove from locks
          const newLocks = new Set(currentState.updateQueryLocks);
          newLocks.delete(lockKey);
          set({ updateQueryLocks: newLocks, isLoading: false });
        } catch (error) {
          // Remove from locks even on error
          const currentState = get();
          const newLocks = new Set(currentState.updateQueryLocks);
          newLocks.delete(lockKey);
          
          // Don't set global error state - just reset loading
          set({ 
            updateQueryLocks: newLocks,
            isLoading: false 
          });
          throw error;
        }
      },

      deleteQuery: async (collectionId: string, queryId: number) => {
        set({ isLoading: true });
        try {
          await CollectionsService.deleteCollectionQuery({ collectionId, queryId });
          
          // Dispatch QUERY_DELETED event
          dispatchCollectionEvent(COLLECTION_EVENTS.QUERY_DELETED, {
            collectionId,
            queryId: queryId.toString()
          });
          
          // Invalidate cache and refresh collection details
          const state = get();
          state.cache.invalidateCollection(collectionId);
          
          try {
            await state.fetchCollectionDetails(collectionId);
          } catch (refreshError) {
            console.error('Failed to refresh collection after query deletion:', refreshError);
            // Clear any error state that fetchCollectionDetails might have set
            set(state => ({ ...state, error: null }));
          }
          
          set({ isLoading: false });
        } catch (error) {
          // Don't set global error state - just reset loading
          set({ isLoading: false });
          throw error;
        }
      },

      moveQuery: async (collectionId: string, queryId: number, targetCollectionId: string, targetFolderId?: string) => {
        set({ isLoading: true });
        try {
          await CollectionsService.moveCollectionQuery({ 
            collectionId, 
            queryId, 
            requestBody: { targetCollectionId, targetFolderId } 
          });
          
          // Invalidate cache for both source and target collections
          const state = get();
          state.cache.invalidateCollection(collectionId);
          if (targetCollectionId !== collectionId) {
            state.cache.invalidateCollection(targetCollectionId);
          }
          
          // Don't refresh here - let the event system handle it
          // This prevents duplicate refreshes and ensures consistent behavior
          
          set({ isLoading: false });
        } catch (error) {
          // Don't set global error state - just reset loading
          set({ isLoading: false });
          throw error;
        }
      },

      createFolder: async (collectionId: string, data: CreateFolder) => {
        // Validate input
        if (!data.name.trim()) {
          const error = new Error('Folder name is required');
          // Don't set global error state - let the form handle it
          throw error;
        }
        
        if (!collectionId.trim()) {
          const error = new Error('Collection ID is required');
          // Don't set global error state - let the form handle it
          throw error;
        }
        
        set({ isLoading: true });
        try {
          const response = await CollectionsService.createFolder({ 
            collectionId, 
            requestBody: data 
          });
          
          // Invalidate cache and refresh collection details
          const state = get();
          state.cache.invalidateCollection(collectionId);
          await state.fetchCollectionDetails(collectionId);
          
          set({ isLoading: false });
          return response.id;
        } catch (error) {
          const errorMessage = parseError(error, 'Failed to create folder');
          
          // Don't set global error state - just reset loading
          set({ isLoading: false });
          throw new Error(errorMessage);
        }
      },

      updateFolder: async (collectionId: string, folderId: string, data: UpdateFolder) => {
        set({ isLoading: true });
        try {
          await CollectionsService.updateFolder({ 
            collectionId, 
            folderId, 
            requestBody: data 
          });
          
          // Invalidate cache and refresh collection details
          const state = get();
          state.cache.invalidateCollection(collectionId);
          
          try {
            await state.fetchCollectionDetails(collectionId);
          } catch (refreshError) {
            console.error('Failed to refresh collection after folder update:', refreshError);
            // Clear any error state that fetchCollectionDetails might have set
            set(state => ({ ...state, error: null }));
          }
          
          set({ isLoading: false });
        } catch (error) {
          // Don't set global error state - just reset loading
          set({ isLoading: false });
          throw error;
        }
      },

      deleteFolder: async (collectionId: string, folderId: string, force = false) => {
        set({ isLoading: true });
        try {
          await CollectionsService.deleteFolder({ collectionId, folderId, force });
          
          // Dispatch FOLDER_DELETED event for immediate UI updates
          dispatchCollectionEvent(COLLECTION_EVENTS.FOLDER_DELETED, {
            collectionId,
            folderId,
            folderName: 'Folder' // We don't have the name here, but event system will handle it
          });
          
          // Invalidate cache and refresh collection details
          const state = get();
          state.cache.invalidateCollection(collectionId);
          
          try {
            await state.fetchCollectionDetails(collectionId, true); // Force refresh
          } catch (refreshError) {
            console.error('Failed to refresh collection after folder deletion:', refreshError);
            // Clear any error state that fetchCollectionDetails might have set
            set(state => ({ ...state, error: null }));
          }
          
          set({ isLoading: false });
        } catch (error) {
          // Don't set global error state - just reset loading
          set({ isLoading: false });
          throw error;
        }
      },

      setError: (error: string | null) => {
        set({ error });
      },

      clearError: () => {
        set({ error: null });
      },
    }),
    {
      name: 'collections-store',
    }
  )
);